import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ShieldCheck, Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = {
  dashboard: "Dashboard", leads: "Leads", customers: "Customers", sales: "Sales",
  purchase_orders: "Purchase Orders", products: "Products", ingredients: "Ingredients",
  recipes: "Recipes & Costing", qc: "Lab & QC", followups: "Follow-ups", tasks: "Tasks",
  notifications: "Notifications", hr: "HR & Employees", accounts: "Accounts (Finance)",
  reports: "Reports", users: "Users", roles: "Roles & Permissions", settings: "Settings",
};

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [sensitive, setSensitive] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: null, name: "", description: "", permissions: {} });

  const load = async () => {
    const { data } = await api.get("/roles");
    setRoles(data.roles); setModules(data.modules); setActions(data.actions); setSensitive(data.sensitive || []);
  };
  useEffect(() => { load(); }, []);

  const toggle = (mod, act) => {
    setForm((f) => {
      const cur = new Set(f.permissions[mod] || []);
      if (cur.has(act)) cur.delete(act); else cur.add(act);
      // 'view' is required for any other action
      if (act !== "view" && cur.size > 0) cur.add("view");
      // If disabling view, clear all
      if (act === "view" && !cur.has("view")) cur.clear();
      return { ...f, permissions: { ...f.permissions, [mod]: Array.from(cur) } };
    });
  };

  const toggleAllForModule = (mod) => {
    setForm((f) => {
      const cur = f.permissions[mod] || [];
      return { ...f, permissions: { ...f.permissions, [mod]: cur.length === actions.length ? [] : [...actions] } };
    });
  };

  const openNew = () => {
    setForm({ id: null, name: "", description: "", permissions: {} });
    setOpen(true);
  };

  const openEdit = (r) => {
    setForm({ id: r.id, name: r.name, description: r.description || "", permissions: r.permissions || {}, is_system: r.is_system });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (form.id) await api.patch(`/roles/${form.id}`, { name: form.name, description: form.description, permissions: form.permissions });
      else await api.post("/roles", { name: form.name, description: form.description, permissions: form.permissions });
      toast.success("Role saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await api.delete(`/roles/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const stats = useMemo(() => {
    const totalPerms = roles.reduce((s, r) => s + Object.values(r.permissions || {}).reduce((a, v) => a + (v?.length || 0), 0), 0);
    return { count: roles.length, system: roles.filter((r) => r.is_system).length, custom: roles.filter((r) => !r.is_system).length, totalPerms };
  }, [roles]);

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Access Control</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-[#C05621]" /> Roles & Permissions
          </h1>
          <p className="text-sm text-[#5C4033] mt-1">Only Super Admins can create custom roles and grant granular module × action permissions.</p>
        </div>
        <Button onClick={openNew} data-testid="role-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> New Role
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total Roles" value={stats.count} />
        <Stat label="System" value={stats.system} accent="text-stone-700" />
        <Stat label="Custom" value={stats.custom} accent="text-[#C05621]" />
        <Stat label="Permissions granted" value={stats.totalPerms} />
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Modules</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => {
              const nMods = Object.keys(r.permissions || {}).filter((k) => (r.permissions[k] || []).length > 0).length;
              return (
                <tr key={r.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`role-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => openEdit(r)}>{r.name}<div className="text-xs text-[#8B7355] font-mono">{r.id}</div></td>
                  <td className="px-4 py-3 text-[#5C4033]">{r.description || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline">{nMods} / {modules.length}</Badge></td>
                  <td className="px-4 py-3">{r.is_system ? <Badge variant="outline" className="bg-stone-100 text-stone-700"><Lock className="w-3 h-3 mr-1 inline" />System</Badge> : <Badge variant="outline" className="bg-[#FEEBC8] text-[#7B341E] border-[#F6AD55]">Custom</Badge>}</td>
                  <td className="px-4 py-3 text-right">
                    {!r.is_system && <button onClick={() => remove(r)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl bg-white max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#C05621]" />
              {form.id ? "Edit Role" : "New Role"}
              {form.is_system && <Badge variant="outline" className="text-xs"><Lock className="w-3 h-3 mr-1 inline" />System role</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role Name *</Label><Input data-testid="role-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Warehouse Manager" disabled={form.id === "super_admin"} /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="uc-label">Module × Action permissions</div>
                <div className="text-xs text-[#8B7355]">Tick to grant; view is auto-included when other actions are enabled.</div>
              </div>
              <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F2E8] uc-label sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Module</th>
                      {actions.map((a) => <th key={a} className="text-center px-2 py-2 capitalize w-20">{a}</th>)}
                      <th className="text-center w-20">All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((m) => {
                      const isSens = sensitive.includes(m);
                      const perms = form.permissions[m] || [];
                      return (
                        <tr key={m} className={`border-t border-[#EAE5D9] ${isSens ? "bg-red-50/40" : ""}`}>
                          <td className="px-3 py-2 font-medium">
                            {MODULE_LABELS[m] || m}
                            {isSens && <span className="ml-1 text-[10px] text-red-700 uppercase tracking-wider">sensitive</span>}
                          </td>
                          {actions.map((a) => (
                            <td key={a} className="text-center px-2 py-2">
                              <Checkbox checked={perms.includes(a)} onCheckedChange={() => toggle(m, a)} disabled={form.id === "super_admin"} data-testid={`role-perm-${m}-${a}`} />
                            </td>
                          ))}
                          <td className="text-center px-2 py-2">
                            <button
                              onClick={() => toggleAllForModule(m)}
                              disabled={form.id === "super_admin"}
                              className="text-xs text-[#C05621] hover:underline disabled:opacity-30"
                            >
                              {perms.length === actions.length ? "None" : "All"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-[#8B7355] mt-2">Sensitive modules (Accounts, Settings, Roles, Users) are shaded — grant access only when needed.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="role-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white" disabled={form.id === "super_admin"}>Save Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
    <div className="uc-label">{label}</div>
    <div className={`font-[Outfit] text-2xl font-semibold mt-1 tracking-tight ${accent || ""}`}>{value}</div>
  </div>
);
